/**
 * Copyright (C) Siemens AG 1996-2024 ALL RIGHTS RESERVED.
 */

import {Component, OnInit, inject} from '@angular/core';
import {IxActiveModal, UploadFileState} from '@siemens/ix-angular';
import {Router} from '@angular/router';
import {PassportService} from '../../../../services/passport.service';
import {ExcelService} from '../../../../services/excel.service';
import {TranslateService} from '@ngx-translate/core';
import {
  AppSettings,
  getExcelConfig,
  ExcelParserConfig,
} from '../../../../models/Constants';
import {BatteryPassCategoryEnum} from '../../../../models/BatteryPassCategoryEnum';
import {SchemaListDTO, SchemaListItemDTO} from '../../../../models/Schema';
import {MasterChildStringEnum} from '../../../../models/Passport';
import {HttpErrorResponse} from '@angular/common/http';

@Component({
  selector: 'app-modal-create-battery-pass',
  templateUrl: './modal-create-battery-pass.component.html',
  styleUrl: './modal-create-battery-pass.component.scss',
  standalone: false,
})
export class ModalCreateBatteryPassComponent implements OnInit {
  private readonly passportService = inject(PassportService);
  private readonly router = inject(Router);
  protected readonly activeModal = inject(IxActiveModal);
  private readonly excelService = inject(ExcelService);
  private readonly translateService = inject(TranslateService);

  category?: BatteryPassCategoryEnum;
  uploadState = UploadFileState.SELECT_FILE;
  uploadErrorMessage = '';
  uploadErrorDetailMessages?: string[] = [];
  showAll = false;
  createFormComplete = false;
  schemasToShow = 3;

  masterChildFlag?: MasterChildStringEnum;
  masterChildFlagIsSet = false;
  workflow?: 'eu';
  workflowIsSet = false;
  method?: 'ui' | 'upload';
  methodIsSet = false;
  schema?: SchemaListItemDTO;
  productIsSet = false;
  categoryIsSet = false;

  loading = true;
  schemas: SchemaListDTO['data'] = [];

  protected readonly MasterChildFlag = MasterChildStringEnum;
  protected readonly BatteryPassCategoryEnum = BatteryPassCategoryEnum;

  ngOnInit(): void {
    this.loading = true;
    this.passportService.loadSchemaList().then((schemas) => {
      this.schemas = schemas.data;
      this.loading = false;
    });
  }

  async onFilesChanged(evt: CustomEvent<File[]>) {
    this.showAll = false;
    this.uploadErrorMessage = '';
    this.uploadErrorDetailMessages = [];
    this.uploadState = UploadFileState.LOADING;

    const file = evt.detail.at(0);

    let passportId: string | undefined = '';

    try {
      // Check file & format
      if (!file || file.type !== AppSettings.EXCEL_FILE_FORMAT) {
        const message = this.translateService.instant(
          'overview.workflow.upload_failed_file_format',
        );

        throw new Error(message);
      }

      // Create passport and navigate to add battery pass
      const passportDto = await this.passportService.create(
        this.masterChildFlag ?? MasterChildStringEnum.CHILD,
        this.schema?.id,
      );

      passportId = passportDto.id;

      if (!passportDto || !passportId) {
        const message = this.translateService.instant(
          'overview.workflow.upload_failed_passport_creation',
        );

        throw new Error(message);
      }

      const config = this.excelConfig;
      if (!config) {
        throw new Error(
          this.translateService.instant(
            'overview.workflow.upload_failed_file_format',
          ),
        );
      }

      const excelPassport = await this.excelService.excelToPassport(
        evt.detail[0],
        config,
        passportDto,
      );

      passportDto.data = excelPassport.data;
      this.passportService.passportsInEdit[passportId] = passportDto;

      await this.passportService.update(passportId, passportDto, undefined);

      await this.router.navigate(['/app/add-passport/' + passportId]);

      this.uploadState = UploadFileState.SELECT_FILE;
      this.activeModal.close('finish');
    } catch (error) {
      if (passportId) {
        await this.passportService.delete(passportId);
      }

      this.handleFileUploadError(error);

      this.uploadState = UploadFileState.UPLOAD_FAILED;
    }
  }

  handleFileUploadError(error: unknown) {
    if (error instanceof Error) {
      let message = error.message;

      if (error.message === 'InvalidTemplate') {
        message = this.translateService.instant(
          'overview.workflow.upload_failed_file_format',
        );
      }

      if (error.message === 'InvalidVersion') {
        message = this.translateService.instant(
          'overview.workflow.upload_failed_file_version',
        );
      }

      this.uploadErrorMessage = message;
    } else if (error instanceof HttpErrorResponse) {
      this.uploadErrorMessage = this.translateService.instant(
        'overview.workflow.upload_failed_see_errors_below',
      );
      if (error.error.errors) {
        this.uploadErrorDetailMessages = error.error.errors;
      } else {
        this.uploadErrorDetailMessages = error.error.message.map(
          (message: {field: string; message: string}) =>
            `${message.field} ${message.message}`,
        );
      }
    } else {
      this.uploadErrorMessage = this.translateService.instant(
        'overview.workflow.upload_failed_passport_creation',
      );
    }
  }

  setProduct(schema: SchemaListItemDTO) {
    this.productIsSet = true;
    this.workflowIsSet = false;
    this.categoryIsSet = false;
    this.masterChildFlagIsSet = false;
    this.methodIsSet = false;
    this.schema = schema;
    this.createFormComplete = this.isComplete();
  }

  setMasterChildFlag(type: MasterChildStringEnum) {
    this.masterChildFlag = type;
    this.masterChildFlagIsSet = true;
    this.methodIsSet = false;
    this.createFormComplete = this.isComplete();
  }

  setWorkflow(workflow: 'eu') {
    this.workflow = workflow;
    this.workflowIsSet = true;
    this.categoryIsSet = false;
    this.masterChildFlagIsSet = false;
    this.methodIsSet = false;
    this.createFormComplete = this.isComplete();
  }

  setMethod(method: 'ui' | 'upload') {
    this.method = method;
    this.methodIsSet = true;
    this.categoryIsSet = false;
    this.createFormComplete = this.isComplete();
  }

  setCategory(category: BatteryPassCategoryEnum) {
    this.category = category;
    this.categoryIsSet = true;
    this.createFormComplete = this.isComplete();
  }

  get excelConfig(): ExcelParserConfig | undefined {
    if (!this.schema) {
      return undefined;
    }
    return getExcelConfig(this.schema.type, this.schema.version);
  }

  /**
   * True when the currently selected schema is version 2.0.0 or later.
   *
   * The `industrialWithoutBms` battery category was introduced with schema
   * 2.0.0 (`batteryCategory` enum gained the `industrialWithoutBms` value)
   * and does not exist in earlier schema versions, so its selector card is
   * only shown for schemas at or above this version.
   */
  get isSchemaV2OrLater(): boolean {
    if (this.schema?.type !== 'battery' || !this.schema.version) {
      return false;
    }
    const major = Number.parseInt(this.schema.version.replace(/^v/i, ''), 10);
    return major >= 2;
  }

  get templateAssetPath(): string | null {
    return this.excelConfig?.templateAssetPath ?? null;
  }

  isComplete(): boolean {
    const baseSet =
      this.productIsSet && this.masterChildFlagIsSet && this.methodIsSet;
    if (this.schema && this.schema.type !== 'battery') {
      return baseSet;
    } else {
      return baseSet && this.workflowIsSet && this.categoryIsSet;
    }
  }

  async createPassportClick() {
    await this.router.navigate(['/app/add-passport'], {
      queryParams: {
        category: this.category,
        type: this.masterChildFlag,
        schemaId: this.schema ? this.schema.id : '',
      },
    });
    this.activeModal.close('finish');
  }
}
